import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import {
  authService,
  AUTH_STATE_CHANGED_EVENT,
  TOKEN_REFRESH_BUFFER_MS,
} from "../services/authService.tsx";
import { API_BASE } from "../config/apiBase";

type AuthContextType = {
  token: string | null;
  userId: string | null;
  role: string | null;
  isLoading: boolean;
  login: (token: string, userId: string, role?: string) => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [token, setToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isActive = true;
    let refreshTimer: ReturnType<typeof window.setTimeout> | undefined;

    const clearRefreshTimer = () => {
      if (refreshTimer !== undefined) {
        window.clearTimeout(refreshTimer);
        refreshTimer = undefined;
      }
    };

    const applyStoredSession = (retryDelayMs = 0) => {
      if (!isActive) return;

      const storedAuth = authService.getAuthData();
      setToken(storedAuth.token);
      setUserId(storedAuth.userId);
      setRole(storedAuth.role);
      clearRefreshTimer();

      if (!storedAuth.token || !storedAuth.userId || !storedAuth.expiryTime) return;

      const expiryTime = Number(storedAuth.expiryTime);
      if (!Number.isFinite(expiryTime)) return;

      const refreshDelay = Math.max(
        retryDelayMs,
        expiryTime - Date.now() - TOKEN_REFRESH_BUFFER_MS
      );
      refreshTimer = window.setTimeout(() => {
        void ensureFreshSession();
      }, refreshDelay);
    };

    const ensureFreshSession = async () => {
      const storedAuth = authService.getAuthData();
      if (!storedAuth.token || !storedAuth.userId) {
        applyStoredSession();
        setIsLoading(false);
        return;
      }

      try {
        if (authService.isTokenExpired()) {
          await authService.refreshToken();
        }
        applyStoredSession();
      } catch {
        // Rejected refresh tokens clear the session inside authService. A
        // transient network/server failure keeps it and retries without a loop.
        applyStoredSession(30 * 1000);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    const handleAuthStateChanged = () => applyStoredSession();
    const handleWindowFocus = () => void ensureFreshSession();
    const handleStorageChange = () => void ensureFreshSession();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void ensureFreshSession();
      }
    };

    window.addEventListener(AUTH_STATE_CHANGED_EVENT, handleAuthStateChanged);
    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("storage", handleStorageChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    void ensureFreshSession();

    return () => {
      isActive = false;
      clearRefreshTimer();
      window.removeEventListener(AUTH_STATE_CHANGED_EVENT, handleAuthStateChanged);
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("storage", handleStorageChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const login = useCallback(async (token: string, userId: string, role?: string) => {
    authService.saveAuthData(
      token,
      userId,
      authService.getTokenExpiryTime(token),
      role
    );
    setToken(token);
    setUserId(userId);
    setRole(role || "user");
  }, []);

  const logout = useCallback(() => {
    authService.clearAuthData();
    setToken(null);
    setUserId(null);
    setRole(null);
    void fetch(`${API_BASE}/logout`, {
      method: "POST",
      credentials: "include",
    }).catch(() => {
      // Local logout must still complete if the server is temporarily offline.
    });
  }, []);

  const value: AuthContextType = {
    token,
    userId,
    role,
    isLoading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!isLoading && children}
    </AuthContext.Provider>
  );
};

// Hook na pohodlné použitie
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
